import { useEffect, useMemo, useRef, useState } from 'react';
import { formatLy, formatSpeed, formatEta, type FleetClient, type RoutePreview, type GalaxyMapResponse } from '@galaxis/shared';
import { previewFleetRoute } from '../../api/galaxy.ts';
import { formatDuration } from './fleet-format.ts';

interface Props {
  fleet: FleetClient;
  map: GalaxyMapResponse | null;
  busy: boolean;
  /** Called with the chosen target; `replace` clears the fleet's queue first. */
  onGo: (targetSystemId: string, replace: boolean) => void;
}

/**
 * Multi-hop route planner. The player picks any known system; the server plans the route over the
 * lanes the player knows and prices it (distance in ly, time at the fleet's speed, arrival, upkeep).
 * Nothing is computed here — unreachable targets and fog are decided by the server.
 */
export function RoutePlanner({ fleet, map, busy, onGo }: Props) {
  const [query, setQuery] = useState('');
  const [target, setTarget] = useState<string | null>(null);
  const [preview, setPreview] = useState<RoutePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const hasQueue = fleet.commands.queued > 0 || !!fleet.commands.automation || !!fleet.commands.active;
  const [replace, setReplace] = useState(hasQueue);
  const seq = useRef(0);

  const name = (id: string) => map?.systems.find(s => s.id === id)?.name ?? '—';
  const candidates = useMemo(() => {
    const q = query.trim().toLocaleLowerCase('hu');
    return (map?.systems ?? [])
      .filter(s => s.id !== fleet.currentSystemId && (!q || s.name.toLocaleLowerCase('hu').includes(q)))
      .sort((a, b) => a.name.localeCompare(b.name, 'hu'))
      .slice(0, 6);
  }, [map, query, fleet.currentSystemId]);

  useEffect(() => {
    if (!target) return;
    const mine = ++seq.current;
    setLoading(true); setError(null); setPreview(null);
    previewFleetRoute(fleet.id, target)
      .then(p => { if (mine === seq.current) setPreview(p); })
      .catch(e => { if (mine === seq.current) setError(e instanceof Error ? e.message : 'Nem sikerült megtervezni az útvonalat.'); })
      .finally(() => { if (mine === seq.current) setLoading(false); });
  }, [target, fleet.id]);

  return (
    <div className="space-y-3" data-testid="route-planner">
      <div>
        <label className="text-xs text-slate-400" htmlFor="route-q">Távoli cél (többugrásos útvonal)</label>
        <input id="route-q" type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Rendszer neve…"
          className="mt-1 w-full rounded-lg bg-slate-900 border border-slate-600 px-3 text-sm text-slate-200" style={{ minHeight: 44 }} />
      </div>
      <ul className="space-y-1" aria-label="Ismert rendszerek">
        {candidates.map(s => (
          <li key={s.id}>
            <button type="button" data-testid="route-target" data-system-id={s.id} aria-pressed={target === s.id} onClick={() => setTarget(s.id)}
              className="w-full text-left rounded-lg border px-3 text-sm flex items-center justify-between gap-2"
              style={{ minHeight: 44, borderColor: target === s.id ? 'rgba(0,229,255,0.7)' : 'rgba(100,116,139,0.4)', color: '#e2e8f0' }}>
              <span className="truncate">{s.name}</span>
              <span className="text-[11px] text-slate-500">{s.discoveryStatus === 'detected' ? 'Meglátogatva' : 'Felmért'}</span>
            </button>
          </li>
        ))}
        {candidates.length === 0 && <li className="text-xs text-slate-500">Nincs ilyen ismert rendszer.</li>}
      </ul>

      {loading && <p className="text-xs text-slate-400" role="status">Útvonal tervezése…</p>}
      {error && <p className="text-xs text-red-300" role="alert" data-testid="route-error">{error}</p>}
      {preview && target && (
        <div className="rounded-xl border border-cyan-500/30 bg-slate-900/60 p-3 space-y-2" data-testid="route-preview">
          <ol className="space-y-1 text-xs">
            <li className="text-slate-400">Indulás: <strong className="text-slate-200">{name(preview.fromSystemId)}</strong></li>
            {preview.hops.map((h, i) => (
              <li key={`${h.systemId}-${i}`} className="flex justify-between gap-2" data-testid="route-hop">
                <span className="text-slate-200 truncate">{i + 1}. {name(h.systemId)}</span>
                <span className="font-mono text-cyan-300 flex-shrink-0">{formatLy(h.distanceLy)} · {formatDuration(h.durationMs)}</span>
              </li>
            ))}
          </ol>
          <dl className="grid grid-cols-2 gap-2 text-xs">
            <div><dt className="text-slate-500">Összesen</dt><dd className="font-mono text-slate-100" data-testid="route-total">{formatLy(preview.totalDistanceLy)} · {formatDuration(preview.totalDurationMs)}</dd></div>
            <div><dt className="text-slate-500">Érkezés</dt><dd className="font-mono text-slate-100" data-testid="route-arrival">{formatEta(preview.arrivesAt)}</dd></div>
            <div><dt className="text-slate-500">Sebesség</dt><dd className="font-mono text-slate-100">{formatSpeed(preview.speedLyPerHour)}</dd></div>
            <div><dt className="text-slate-500">Fenntartás útközben</dt><dd className="font-mono text-slate-100">{preview.upkeepMetalPerHour} fém/óra</dd></div>
          </dl>
          {hasQueue && (
            <label className="flex items-center gap-2 text-xs text-slate-300" style={{ minHeight: 44 }}>
              <input type="checkbox" checked={replace} onChange={e => setReplace(e.target.checked)} style={{ width: 20, height: 20 }} />
              A meglévő parancsok törlése, indulás azonnal
            </label>
          )}
          <button type="button" disabled={busy} onClick={() => onGo(target, replace)} data-testid="route-go"
            className="w-full rounded-lg text-sm font-semibold disabled:opacity-40"
            style={{ minHeight: 44, background: 'rgba(0,229,255,0.14)', color: '#00e5ff', border: '1px solid rgba(0,229,255,0.5)' }}>
            Útvonal indítása → {name(target)}
          </button>
        </div>
      )}
    </div>
  );
}
